/**
 * The Homestead Score, and every one of its thirteen factors.
 *
 * ── §6.2, which is the whole reason this panel is long ───────────────────────
 *
 * The score renormalises over APPLICABLE factors. That is the correct
 * arithmetic for a missing input and it has a nasty property: an opportunity
 * nobody knows anything about can score very high, because only the factors
 * that happen to look good are in the denominator. Confidence is the honest
 * counterweight — and a counterweight only works if it is on the screen, next
 * to the number, every time. Hence "Homestead Score 92 · confidence 100% — 13
 * of 13 factors have data", never the bare 92.
 *
 * ── All thirteen, always ─────────────────────────────────────────────────────
 *
 * The server returns the factors it STORED. A factor can be missing from that
 * list — the sweep has not run, the engine version moved, the row was pruned —
 * and a panel that renders only what came back would silently show nine
 * factors and look complete. So the thirteen keys are the spine and the
 * response is joined onto it; a factor with no row says so.
 *
 * Three states, and they are not the same thing:
 *
 *  - **omitted** (`applicable = false`) — there is no honest way to score it,
 *    so it is out of the average entirely.
 *  - **penalty** (`applicable = true`, `hasData = false`) — §6.1 gives three of
 *    these. An unstated condition on a used trailer is usually bad news, and
 *    scoring it neutral would reward hiding it.
 *  - **scored** — a sub-score, from real inputs.
 *
 * ── The sentences are English in both locales ────────────────────────────────
 *
 * `sentence` is STORED as generated, because regenerating it later against a
 * changed assumption library produces a sentence that never existed. It is a
 * record, not a UI string. The factor's NAME is translated; its stored
 * sentence is quoted.
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import {
  cappedBy,
  dateTimeText,
  percent,
  score as scoreText,
  scoreLine,
  scoreTone,
  TONES,
} from '../../lib/format'
import { FACTOR_KEYS, factorLabel, provenanceLabel, riskLevelLabel } from '../../lib/domain'
import { Chip, Empty, Meter, Panel, S } from './ui'
import type { ScoreFactor, ScoreView } from './types'

const EN = {
  title: 'Score',
  notScored: 'This has not been scored yet.',
  notScoredWhy: 'Scoring runs on a sweep and after any change that matters. It will appear here.',
  unavailable: 'The score could not be loaded.',
  weight: 'weight',
  omitted: 'Not scored — nothing to score it from',
  penalty: 'Counted as a penalty — this is unknown, and unknown is bad news here',
  noRow: 'No factor row was stored for this one.',
  computed: 'Computed',
  engine: 'Engine',
  weights: 'Weights',
  alert: 'Passes the alert threshold',
  ofData: 'have data',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Puntuación',
  notScored: 'Esto todavía no se ha puntuado.',
  notScoredWhy: 'La puntuación corre en un barrido y después de cualquier cambio importante. Va a aparecer aquí.',
  unavailable: 'No se pudo cargar la puntuación.',
  weight: 'peso',
  omitted: 'Sin puntuar — no hay con qué puntuarlo',
  penalty: 'Cuenta como penalidad — esto no se sabe, y no saberlo aquí es mala señal',
  noRow: 'No se guardó una fila para este factor.',
  computed: 'Calculada',
  engine: 'Motor',
  weights: 'Pesos',
  alert: 'Pasa el umbral de alerta',
  ofData: 'tienen datos',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

export default function ScoreCard({ score, failed = false }: { score: ScoreView | null; failed?: boolean }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  const stored = new Map((score?.factors ?? []).map((f) => [f.key, f] as const))
  const withData = (score?.factors ?? []).filter((f) => f.hasData).length
  const tone = scoreTone(score?.score ?? null)
  const cap = cappedBy(score?.rawScore ?? null, score?.score ?? null, locale)

  return (
    <Panel
      id="score"
      title={t('title')}
      defaultOpen
      badge={
        <Chip tone={tone} title={t('title')}>
          {scoreText(score?.score ?? null)}
        </Chip>
      }
    >
      {failed ? (
        <Empty>{t('unavailable')}</Empty>
      ) : !score || score.score === null ? (
        <>
          <Empty>{t('notScored')}</Empty>
          <p style={S.note}>{t('notScoredWhy')}</p>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
            <span style={{ ...S.huge, color: tone.fg }}>{scoreText(score.score)}</span>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              {percent(score.confidence, locale)} · {withData}/{FACTOR_KEYS.length} {t('ofData')}
            </span>
          </div>

          {/* §6.2: never the bare number. */}
          <p style={S.sentence}>
            {scoreLine(
              { score: score.score, confidence: score.confidence, withData, total: FACTOR_KEYS.length },
              locale,
            )}
          </p>

          {score.summary && <p style={{ ...S.sentence, marginTop: 8 }}>{score.summary}</p>}

          <div style={{ ...S.chipRow, marginTop: 10 }}>
            <Chip tone={score.riskLevel === 'high' ? TONES.danger : score.riskLevel === 'caution' ? TONES.warn : TONES.quiet}>
              {riskLevelLabel(score.riskLevel, locale)}
            </Chip>
            {score.alertQualified && <Chip tone={TONES.strong}>{t('alert')}</Chip>}
          </div>

          {/* The cap, said out loud. `raw_score` exists precisely so this
              sentence can be written and so clearing a flag restores the real
              number instantly. */}
          {cap && (
            <p style={{ ...S.note, color: 'var(--danger)', fontWeight: 600 }}>{cap}</p>
          )}

          <div style={{ marginTop: 14 }}>
            {FACTOR_KEYS.map((key) => (
              <FactorRow key={key} factorKey={key} factor={stored.get(key)} locale={locale} t={t} />
            ))}
          </div>

          <p style={{ ...S.note, marginTop: 12 }}>
            {t('computed')} {dateTimeText(score.computedAt, locale)} · {t('engine')} {score.engineVersion ?? '—'} ·{' '}
            {t('weights')} {score.weightsVersion ?? '—'}
          </p>
        </>
      )}
    </Panel>
  )
}

function FactorRow({
  factorKey,
  factor,
  locale,
  t,
}: {
  factorKey: string
  factor: ScoreFactor | undefined
  locale: Locale
  t: (key: CopyKey) => string
}) {
  const omitted = factor !== undefined && !factor.applicable
  const penalty = factor !== undefined && factor.applicable && !factor.hasData
  const tone = omitted ? TONES.quiet : penalty ? TONES.warn : scoreTone(factor?.subScore ?? null)

  return (
    <div style={{ padding: '10px 0', borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{factorLabel(factorKey, locale)}</span>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>
          {factor ? `${factor.weight} ${t('weight')}` : ''}
        </span>
        <span style={{ fontSize: 15, fontWeight: 800, color: tone.fg, minWidth: 34, textAlign: 'right' }}>
          {omitted || penalty ? '—' : scoreText(factor?.subScore ?? null)}
        </span>
      </div>

      <Meter value={omitted || penalty ? 0 : factor?.subScore ?? null} tone={tone} />

      {factor ? (
        <>
          <p style={{ ...S.sentence, marginTop: 6 }}>{factor.sentence}</p>
          <div style={{ ...S.chipRow, marginTop: 6 }}>
            {omitted && <Chip tone={TONES.quiet} outlined>{t('omitted')}</Chip>}
            {penalty && <Chip tone={TONES.warn}>{t('penalty')}</Chip>}
            {factor.provenance && (
              <Chip tone={TONES.quiet} outlined>
                {provenanceLabel(factor.provenance, locale)}
              </Chip>
            )}
          </div>
        </>
      ) : (
        <p style={{ ...S.note, marginTop: 6 }}>{t('noRow')}</p>
      )}
    </div>
  )
}
